/**
 * Router Configuration
 * Minimal router - handles base app route and auth
 *
 * Architecture:
 * - Router: Handles page visibility (/, /discovery, /library)
 * - ModeOrchestrator: Handles mode logic (session creation, welcome messages)
 * - PageManager: Controls which page element is visible
 *
 * ============================================================================
 * ROUTING PATTERNS - Which to use when:
 * ============================================================================
 *
 * PATTERN A: State-based navigation (for components that set context before routing)
 *   Use when: Dashboard cards, sidebar clicks, activity items
 *   How: Set app.pendingNavigation, then call page.redirect('/')
 *   Example:
 *     app.pendingNavigation = { mode: 'DISCOVERY', pathId: '...' };
 *     page.redirect('/');
 *
 * PATTERN B: Event-based navigation (for components that emit navigation events)
 *   Use when: Action buttons, library cards, anywhere you'd emit an event
 *   How: Emit 'navigate' event with page and context
 *   Example:
 *     eventBus.emit('navigate', { page: 'chat', mode: 'ADVICE', entryType: 'discuss_pattern', itemId: '...' });
 *
 * PATTERN C: Direct mode switching (for in-place mode changes on chat page)
 *   Use when: Mode dropdown, header mode selector - user already on chat
 *   How: Call modeOrchestrator.switchMode() directly
 *   Example:
 *     await modeOrchestrator.switchMode('ADVICE');
 *
 * Both Pattern A and B converge to the same flow - they're stylistically
 * different but functionally equivalent. Pattern C is for mode-only changes.
 *
 * State modified by navigation:
 * - app.pendingNavigation: Set by navigation initiators, cleared by handlers
 * - app.returnSession: Set when leaving chat, used to restore on return
 * ============================================================================
 *
 * For previous URL-based navigation patterns, see:
 * project-docs/temporary/ARCHIVE_Router_Sidebar_Navigation.md
 */

import page from './vendor/page.js'
import { eventBus } from './core/utils.js'
import { sessionManager } from './core/SessionManager.js'
import { URLParamsHandler } from './utils/URLParamsHandler.js'
import { InsightsSection } from './sections/InsightsSection.js'
import { PracticesSection } from './sections/PracticesSection.js'
import { SessionsSection } from './sections/SessionsSection.js'
import { DiscoverySection } from './sections/DiscoverySection.js'
import { PartnerNotesSection } from './sections/PartnerNotesSection.js'
import { PartnerSection } from './sections/PartnerSection.js'
import { SubscriptionAPI } from './core/subscriptionApi.js'
import { Tooltip } from './utils/Tooltip.js'

/**
 * Initialize router with middleware and routes
 * Called by app.js after all modules initialized
 * @param {App} app - Main app instance with access to all modules
 */
// The pathname the document was served with. Module scope, so it is captured
// at load — the same moment index.html's gtag('config') sends its automatic
// page_view — and not later, when initRouter may run against a URL the user
// has already navigated away from.
const DOCUMENT_LOAD_PATHNAME = typeof window !== 'undefined' ? window.location.pathname : null;

export function initRouter(app) {
  // ===== EVENT-BASED NAVIGATION =====
  // Allows components to navigate using eventBus.emit('navigate', { page, mode, ...data })
  eventBus.on('navigate', async (data) => {
    const { page: targetPage, mode, ...rest } = data;

    console.log('[Router] Navigate event received:', { targetPage, mode, ...rest });

    // Set pending navigation for the target page handler
    if (mode || Object.keys(rest).length > 0) {
      app.pendingNavigation = { mode, ...rest };
    }

    // Navigate to the appropriate route
    if (targetPage === 'dashboard') {
      // Explicit dashboard intent: clear mode/return-session state so
      // handleBaseRoute falls through to dashboard rather than preserving
      // the current chat mode (Priority 3 in handleBaseRoute would otherwise
      // keep chat visible when a mode is still active).
      app.pendingNavigation = null;
      app.returnSession = null;
      if (app.modules.modeOrchestrator?.getCurrentMode?.()) {
        app.modules.modeOrchestrator.returnToDashboard();
      }
      page('/');
    } else if (targetPage === 'chat' || targetPage === '/') {
      page('/');
    } else if (targetPage === 'practices' || targetPage === 'experiments') {
      page('/experiments');
    } else if (targetPage === 'insights' || targetPage === 'library') {
      page('/library');
    } else if (targetPage === 'upgrade') {
      page('/upgrade');
    } else if (targetPage === 'subscription') {
      page('/subscription');
    } else if (targetPage === 'discovery') {
      page('/discovery');
    } else if (targetPage === 'partner') {
      page('/partner');
    } else if (targetPage === 'notes') {
      page('/notes');
    } else if (targetPage === 'bridge') {
      page('/bridge');
    } else if (targetPage === 'account-settings') {
      page('/account-settings');
    } else if (targetPage === 'help') {
      page('/help');
    } else if (targetPage === 'memory') {
      page('/memory');
    } else if (targetPage === 'search') {
      const sessionParam = rest.sessionNumber ? `?session=${rest.sessionNumber}` : '';
      page(`/search${sessionParam}`);
    } else {
      console.warn('[Router] Unknown target page:', targetPage);
      page('/');
    }
  });

  // ===== GLOBAL MIDDLEWARE (runs on ALL routes) =====

  // Middleware 1: Close panels before navigation and track page view
  // Track the previous pathname so we can distinguish cross-page navigation
  // from intra-section query-only changes (e.g., /search ↔ /search?session=N
  // for panel-routed detail open/close/swap).
  let lastPathname = null;

  // index.html's gtag('config') already sends a page_view for the document
  // load, so the first dispatch here would count that route twice — but ONLY
  // when it lands on the same pathname. An unauthenticated visitor loads
  // /login, converts, and only then reaches initRouter, whose first dispatch
  // is the dashboard: a different page that gtag has not counted, and the one
  // immediately after the conversion we most want to see. Suppress the double,
  // not the post-signup landing.
  let firstDispatchSeen = false;

  // Pathname → URL param name(s) that open a routed right-panel detail on
  // that page (mirrors each section's own urlParamSchema, but only the
  // detail-opening keys — a filter param like `type` doesn't belong here).
  // Used solely to recognize a cross-page nav that's swapping one routed
  // detail for an equivalent one elsewhere (e.g. a starred-row click from a
  // session to a note), so the panel can stay open for that handoff instead
  // of a full close/reopen — see RightPanelManager.beginHandoff().
  const PANEL_ROUTED_PARAMS = {
    '/search': ['session', 'note', 'guide'],
    '/notes': ['note'],
    '/discovery': ['thread'],
    '/library': ['topic', 'pattern', 'guide', 'sharedGuide', 'framework']
  };

  function destinationReopensPanel(pathname, querystring) {
    const keys = PANEL_ROUTED_PARAMS[pathname];
    if (!keys) return false;
    const params = new URLSearchParams(querystring);
    return keys.some((key) => params.has(key));
  }

  page('*', (ctx, next) => {
    console.log('[NAV-Router] Middleware running for path:', ctx.path);

    // Dismiss any visible tooltip before the route changes. Tooltips anchor to
    // an element's mouseleave to hide; when a navigation swaps out or rewrites
    // that element (e.g. the dashboard's innerHTML rebuild of the privacy
    // button), the detached node can never fire mouseleave, orphaning the
    // tooltip on document.body. Clearing here at the single nav choke-point
    // guarantees no tooltip survives a page transition.
    Tooltip.removeAll();

    // Track page view for analytics (SPA navigation). `ctx.pathname`, never
    // `ctx.path` — page.js leaves the querystring on the latter, and
    // trackPageView bypasses sanitizeParams, so `/reset-password?token=…`
    // would reach GA4 verbatim.
    const duplicatesConfigHit = !firstDispatchSeen && ctx.pathname === DOCUMENT_LOAD_PATHNAME;
    firstDispatchSeen = true;
    if (!duplicatesConfigHit && window.analyticsService) {
      window.analyticsService.trackPageView(ctx.pathname);
    }

    // Close right panel ONLY on actual page transitions. When the pathname
    // is unchanged (only the querystring changed), this is an intra-section
    // navigation — typically panel-routed detail open/close/swap — and the
    // section drives its own panel state; closing here would clobber it.
    const isCrossPageNav = lastPathname !== null && lastPathname !== ctx.pathname;
    if (isCrossPageNav && app.modules.rightPanelManager?.isOpen()) {
      if (destinationReopensPanel(ctx.pathname, ctx.querystring)) {
        // The destination page will immediately reopen an equivalent
        // detail — arm a handoff instead of closing, so the panel stays
        // open and the destination's open() cross-fades in place. If the
        // destination can't resolve the item, its own not-found fallback
        // calls closeStaleHandoff() as a safety net.
        console.log('[NAV-Router] Arming right-panel handoff for cross-page nav');
        app.modules.rightPanelManager.beginHandoff();
      } else {
        console.log('[NAV-Router] Closing right panel before navigation');
        app.modules.rightPanelManager.close();
      }
    }
    lastPathname = ctx.pathname;

    // 0-message-session policy: leaving the chat surface discards a report
    // conversation the user never spoke in (ModeOrchestrator owns the
    // predicate; this is only the "are we leaving chat?" half). Two shapes of
    // leaving: a real page change, and a '/' dispatch that will land on the
    // dashboard rather than back on chat. Entering chat from a page is NOT a
    // leave — the chat page isn't active yet when this middleware runs.
    if (app.modules.pageManager?.isPageActive('chat')
        && (ctx.pathname !== '/' || !willBaseRouteShowChat(app))) {
      app.modules.modeOrchestrator?.discardEmptyCheckinSession?.();
    }

    next();
  });

  // Middleware 2: Reserved for future navigation hooks
  page('*', async (ctx, next) => {
    next();
  });

  // ===== ROUTE DEFINITIONS =====

  // Base route - shows chat interface (with dashboard as welcome state)
  page('/', async (ctx) => {
    await handleBaseRoute(app, ctx);
  });

  // Chat route - redirect to base route
  page('/chat', () => {
    page.redirect('/');
  });

  // Retired route (feature removed 2026-07): old bookmarks/history land on
  // the Search page (the session-notes browsing surface after the /sessions →
  // /search repurpose).
  page('/focus-areas', () => {
    // Clear up-front: this handler doesn't consume pendingNavigation, and
    // clearing before the redirect means stale state can't leak into a
    // later handleBaseRoute call. `?issue=` params are deliberately dropped.
    app.pendingNavigation = null;
    page.redirect('/search');
  });

  // Experiments page (internal page id stays `practices`)
  page('/experiments', async (ctx) => {
    await handlePracticesPage(app, ctx);
  });

  // Library page (backed by the existing InsightsSection)
  page('/library', async (ctx) => {
    await handleInsightsPage(app, ctx);
  });

  // Upgrade page (pricing/subscription)
  page('/upgrade', async () => {
    await handleUpgradePage(app);
  });

  // Subscription management page (Premium users)
  page('/subscription', async (ctx) => {
    await handleSubscriptionPage(app, ctx);
  });

  // Discovery page (was Share)
  page('/discovery', async (ctx) => {
    await handleDiscoveryPage(app, ctx);
  });

  // Profile-menu placeholder pages
  page('/partner', async (ctx) => {
    await handlePartnerPage(app, ctx);
  });

  // Notes page (couple-gated inbox/sent partner notes, panel-routed note detail)
  page('/notes', async (ctx) => {
    await handlePartnerNotesPage(app, ctx);
  });

  page('/bridge', async () => {
    await handleBridgePage(app);
  });

  page('/account-settings', async () => {
    await handleAccountSettingsPage(app);
  });

  page('/help', async () => {
    await handleHelpPage(app);
  });

  page('/memory', async () => {
    await handleMemoryPage(app);
  });

  // Legacy public route: the merged page is now canonical at `/library`.
  // Preserve stale bookmark/deep-link query strings exactly (page.js
  // client-side SPA routing, not an HTTP 302).
  page('/insights', (ctx) => {
    app.pendingNavigation = null;
    const querySuffix = ctx.querystring ? `?${ctx.querystring}` : '';
    page.redirect(`/library${querySuffix}`);
  });

  // Legacy public route: experiments were previously at /practices.
  // Preserve stale bookmark/deep-link query strings exactly (page.js
  // client-side SPA routing, not an HTTP 302).
  page('/practices', (ctx) => {
    app.pendingNavigation = null;
    const querySuffix = ctx.querystring ? `?${ctx.querystring}` : '';
    page.redirect(`/experiments${querySuffix}`);
  });

  page('/search', async (ctx) => {
    await handleSearchPage(app, ctx);
  });

  // Login page (for authenticated users visiting /login)
  page('/login', (ctx) => {
    handleLoginRoute(app, ctx);
  });

  // Try free - dedicated guest entry point
  // Use this for marketing/ad traffic to bypass login and create guest session
  page('/try-free', async () => {
    await handleTryFreeRoute(app);
  });

  // Subscription success - post-checkout return from Stripe
  page('/subscription/success', async () => {
    await handleSubscriptionSuccess(app);
  });

  // Password reset - accessed via email link with token
  page('/reset-password', () => {
    handleResetPasswordRoute(app);
  });

  // Catch-all: redirect to base route
  page('*', (ctx) => {
    console.warn(`[Router] Unknown route: ${ctx.path}, redirecting to /`);
    page.redirect('/');
  });

  // Start router
  page();
}

// ===== ROUTE HANDLERS =====

/**
 * Handle base route (/)
 * The single page of the app - chat interface with dashboard as welcome state
 *
 * Navigation scenarios (in priority order):
 * 0. Orientation in progress → Stay on chat (dashboard blocked)
 * 1. pendingNavigation set → Process the pending state (mode, path, etc.)
 * 2. Valid returnSession exists → Restore previous session
 * 3. Active mode from another page → Preserve current mode and show chat
 * 4. Default → Show dashboard (welcome state)
 */
async function handleBaseRoute(app, ctx) {
  // Priority 0: Block default dashboard navigation during orientation.
  // Yields to Priority 1 when the user has expressed *explicit* ORIENTATION
  // navigation intent (e.g., clicking "Finish Orientation" on the dashboard
  // after dismissing) — that path needs Priority 1 to perform the mode switch
  // and pass `resumeFrom` / `onboardingData` through to OrientationMode.onEnter.
  const isExplicitOrientationResume = hasPendingNavigation(app)
    && app.pendingNavigation.mode === 'ORIENTATION';

  if (isOrientationInProgress(app) && !isExplicitOrientationResume) {
    console.log('[Router] Orientation in progress - staying on chat, dashboard blocked');
    showChatPage(app);
    return;
  }

  // Priority 1: Explicit navigation with context (card click, sidebar, etc.)
  if (hasPendingNavigation(app)) {
    showChatPage(app);
    await processPendingNavigation(app);
    return;
  }

  // Priority 1.5: Post-Stripe-checkout resume. If App._rehydrateCheckoutReturnSession
  // found a fresh snapshot, drop the user back into the exact conversation
  // they were in when they clicked "Upgrade for unlimited". Distinct from
  // `returnSession` (Priority 2) to avoid the dashboard save-on-show mechanism
  // clobbering this intent.
  if (app.checkoutReturnSession) {
    const { mode, sessionId } = app.checkoutReturnSession;
    app.checkoutReturnSession = null; // consume

    // Show chat BEFORE awaiting resumeSession. resumeSession used to reach
    // into `window.app` for page visibility, but on cold checkout return
    // initRouter dispatches this handler before `window.app = this` runs,
    // so the optional chain would silently no-op and leave the user on
    // dashboard with chat state in memory.
    showChatPage(app);

    try {
      const resumed = await app.modules.modeOrchestrator.resumeSession(mode, sessionId);
      // Underscores only — GA4 rejects an event name containing a colon
      // outright. Params must match sanitizeParams' allowlist or they are
      // dropped before the hit.
      window.analyticsService?.trackEvent?.('subscription_resumed_session', {
        mode,
        message_count: resumed?.messageCount ?? 0
      });
      return;
    } catch (err) {
      console.warn('[Router] Checkout session resume failed:', err?.message || err);
      window.notificationManager?.showInfo(
        "Couldn't resume your previous chat. You can find it in Sessions.",
        6000
      );
      // Fall through to the remaining priorities (usually dashboard).
    }
  }

  // Priority 2: Returning to app - restore previous session
  if (hasValidReturnSession(app)) {
    showChatPage(app);
    await restoreReturnSession(app);
    return;
  }

  // Priority 3: Preserve current mode if coming FROM another page
  if (shouldPreserveCurrentMode(app)) {
    console.log('[Router] Preserving current mode:', app.modules.modeOrchestrator?.getCurrentMode());
    showChatPage(app);
    return;
  }

  // Priority 4: Show dashboard (default)
  saveSessionForPotentialReturn(app);
  console.log('[Router] Showing dashboard');
  showDashboard(app);
}

/**
 * Check if orientation onboarding is in progress
 * During orientation, dashboard is blocked and user stays on chat
 *
 * @param {App} app - App instance
 * @returns {boolean} True if orientation is incomplete
 */
function isOrientationInProgress(app) {
  // Check 1: Already in orientation mode (runtime state)
  if (app.modules.modeOrchestrator?.getCurrentMode() === 'ORIENTATION') {
    return true;
  }

  // Check 2: User needs orientation AND has not dismissed it.
  // Mirrors needsOrientation() in app.js - kept inline to avoid circular imports.
  // null/undefined onboardingStep = new user who hasn't started orientation yet (still needs it).
  // Dismissed users can browse the dashboard freely; FeatureToggleService keeps
  // the rest of the app locked until orientation is actually complete.
  const currentUser = app.currentUser;
  if (currentUser && currentUser.onboardingStep !== 'complete') {
    if (currentUser.orientationDismissedAt) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * Check if there's pending navigation to process
 */
function hasPendingNavigation(app) {
  return !!app.pendingNavigation;
}

/**
 * Check if there's a valid return session to restore
 */
function hasValidReturnSession(app) {
  return app.returnSession && isReturnSessionValid(app.returnSession);
}

/**
 * Check if we should preserve the current mode (user navigating from another page)
 * Returns true if: has active mode AND not already on chat
 */
function shouldPreserveCurrentMode(app) {
  const currentMode = app.modules.modeOrchestrator?.getCurrentMode();
  const isAlreadyOnChat = app.modules.pageManager?.isPageActive('chat');
  return currentMode && !isAlreadyOnChat;
}

/**
 * Predict whether navigating to the base route (`/`) will land on the chat
 * surface (true) or the dashboard (false), WITHOUT navigating. Reuses the same
 * chat-showing predicates as handleBaseRoute so the two never drift. Scoped to
 * the steady state: the transient priorities (pendingNavigation, checkout
 * resume) aren't in flight while a user idly hovers a full-page's close button,
 * so they're intentionally omitted. Used by the page toolbar's close-button
 * tooltip to read "Back to chat" vs "Back to dashboard".
 *
 * @param {App} app - App instance
 * @returns {boolean} True if `/` would show chat; false if it would show the dashboard
 */
export function willBaseRouteShowChat(app) {
  if (!app) return false;
  return isOrientationInProgress(app)
    || hasValidReturnSession(app)
    || shouldPreserveCurrentMode(app);
}

/**
 * Save current session for potential restoration when returning from dashboard
 * Only saves if user is on chat with an active session
 */
function saveSessionForPotentialReturn(app) {
  const currentMode = app.modules.modeOrchestrator?.getCurrentMode();
  const isAlreadyOnChat = app.modules.pageManager?.isPageActive('chat');

  if (isAlreadyOnChat && currentMode) {
    // Privacy Mode: never save a private session for return. Leaving the chat
    // for the dashboard abandons it (same convention as reload — all reattach
    // paths are gated), and PrivacyModeManager resets its UI state when the
    // dashboard shows, so a restore here would re-enter the private session
    // with no privacy indicator. Must run BEFORE that reset (it does: this is
    // called ahead of showDashboard in handleBaseRoute).
    if (app.modules.privacyModeManager?.getIsPrivacyMode?.()) {
      console.log('[Router] Private session — not saved for return');
      return;
    }
    const sessionId = window.sessionManager?.getCurrentSessionId();
    if (sessionId) {
      app.returnSession = {
        mode: currentMode,
        sessionId: sessionId,
        timestamp: Date.now()
      };
      console.log('[Router] Saved session for return:', app.returnSession);
    }
  }
}

/**
 * Show dashboard via PageManager
 * @param {App} app - App instance
 */
async function showDashboard(app) {
  // Use PageManager to show dashboard (controls visibility)
  if (app.modules.pageManager) {
    app.modules.pageManager.showDashboard();
  }
  // Use DashboardManager to load/render content
  if (app.modules.dashboardManager) {
    await app.modules.dashboardManager.returnToDashboard();
  }
}

/**
 * Check if return session is still valid
 * @param {Object} returnSession - Stored return session
 * @returns {boolean} True if session can be restored
 */
function isReturnSessionValid(returnSession) {
  if (!returnSession?.mode || !returnSession?.sessionId) {
    return false;
  }

  // Session must be less than 30 minutes old
  // (Backend orphan cleanup runs at 60 min, so 30 min gives safety margin)
  const MAX_AGE_MS = 30 * 60 * 1000;
  const age = Date.now() - returnSession.timestamp;

  if (age > MAX_AGE_MS) {
    console.log('[Router] Return session expired:', Math.round(age / 1000 / 60), 'minutes old');
    return false;
  }

  return true;
}

/**
 * Restore the user's previous session when returning from dashboard
 * @param {App} app - App instance
 */
async function restoreReturnSession(app) {
  const { mode, sessionId } = app.returnSession;

  // Clear returnSession now that we're using it
  app.returnSession = null;

  console.log('[Router] Restoring return session:', mode, sessionId);

  try {
    // Restore the mode display (header) without creating new session
    if (app.modules.modeOrchestrator) {
      await app.modules.modeOrchestrator.restoreMode(mode);
    }
    console.log('[Router] Session restored successfully');
  } catch (error) {
    // Fallback: create new session if restoration fails
    console.warn('[Router] Session restoration failed, creating new session:', error.message);
    await app.modules.modeOrchestrator?.switchMode('ADVICE');
  }
}

/**
 * Show chat page
 */
function showChatPage(app) {
  if (app.modules.pageManager) {
    app.modules.pageManager.showPage('chat');
  }
}

/**
 * Process pending navigation state
 *
 * ARCHITECTURE: Router handles page visibility only, delegates mode logic to ModeOrchestrator
 *
 * The router emits a single 'navigation:requested' event with all context data.
 * ModeOrchestrator's handleNavigation() method decides how to process it:
 * - Session restoration
 * - Discovery with module (suppresses welcome, loads questionnaire)
 * - Insight with specific ID (suppresses welcome, loads insight)
 * - Generic mode switch (shows welcome)
 *
 * This separation ensures:
 * - Single source of truth for mode transition logic
 * - No duplicate welcome messages or double mode switches
 * - Clear responsibility boundaries (Router = pages, ModeOrchestrator = modes)
 *
 * @param {App} app - App instance
 */
async function processPendingNavigation(app) {
  const pending = app.pendingNavigation;
  app.pendingNavigation = null; // Clear immediately to prevent reprocessing

  console.log('[NAV-Router] Processing pending navigation:', pending);

  // Delegate ALL mode logic to ModeOrchestrator via event
  // ModeOrchestrator handles: session restoration, welcome messages, mode-specific flows
  if (pending.mode) {
    eventBus.emit('navigation:requested', pending);
  }
}

/**
 * Handle /reset-password route
 * Shows login overlay with password reset view
 * AuthManager.checkForResetToken() will detect the token and show the reset form
 */
function handleResetPasswordRoute(app) {
  console.log('[Router] /reset-password route accessed');

  // Show login overlay - AuthManager will detect the token in URL
  // and display the password reset form automatically
  app.showLoginOverlay();
}

/**
 * Handle /login route
 * If user is fully authenticated (not a guest), redirect to dashboard
 * If guest or unauthenticated, show login overlay
 */
function handleLoginRoute(app, ctx) {
  console.log('[Router] /login route accessed');

  // If user is fully authenticated (not a guest), redirect to dashboard
  if (app.currentUser && !app.currentUser.isGuest) {
    console.log('[Router] User already authenticated, redirecting to dashboard');
    page.redirect('/');
    return;
  }

  // Guest or unauthenticated - show login overlay
  console.log('[Router] Showing login overlay for', app.currentUser?.isGuest ? 'guest' : 'unauthenticated user');
  app.showLoginOverlay();
}

/**
 * Handle /experiments route (internal page id stays `practices`)
 * Shows experiments page via PageManager and activates PracticesSection
 * @param {App} app - App instance
 * @param {Object} ctx - page.js context with querystring
 */
async function handlePracticesPage(app, ctx) {
  console.log('[Router] Showing practices page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('practices');
  }
  if (!activated) return;

  if (app.modules.practicesSection) {
    await app.modules.practicesSection.activate();

    // Always run applyURLParams — empty params is the close signal, and the
    // status filter has a default that benefits from being applied even when
    // the URL has no explicit ?status=.
    const urlParams = URLParamsHandler.parse(ctx.querystring, PracticesSection.urlParamSchema);
    await app.modules.practicesSection.applyURLParams(urlParams.values);
  }
}

/**
 * Handle /library route
 * Shows the Library page via PageManager and activates InsightsSection — the
 * merged page carrying Topics (hidden, deep-link only), Patterns,
 * Assessments, and Guides tabs.
 * @param {App} app - App instance
 * @param {Object} ctx - page.js context with querystring
 */
async function handleInsightsPage(app, ctx) {
  console.log('[Router] Showing library page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('insights');
  }
  if (!activated) return;

  if (app.modules.insightsSection) {
    await app.modules.insightsSection.activate();

    // Always run applyURLParams — empty params is the close signal for the
    // detail panel; tab change pushes also re-fire this handler.
    const urlParams = URLParamsHandler.parse(ctx.querystring, InsightsSection.urlParamSchema);
    await app.modules.insightsSection.applyURLParams(urlParams.values);
  }
}

/**
 * Handle /upgrade route
 * Shows upgrade/pricing page via PageManager and activates UpgradeSection
 */
async function handleUpgradePage(app) {
  console.log('[Router] Showing upgrade page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('upgrade');
  }
  if (!activated) return;

  if (app.modules.upgradeSection) {
    await app.modules.upgradeSection.activate();
  }
}

/**
 * Handle /subscription route
 * Shows the subscription management page for Premium users.
 * ?refresh=1 triggers a short polling window after returning from a
 * state-changing Stripe Portal flow (cancel, resume, payment update).
 *
 * @param {App} app - App instance
 * @param {Object} ctx - page.js context with querystring
 */
async function handleSubscriptionPage(app, ctx) {
  console.log('[Router] Showing subscription page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('subscription');
  }
  if (!activated) return;

  if (app.modules.subscriptionSection) {
    const refresh = new URLSearchParams(ctx.querystring || '').get('refresh') === '1';
    await app.modules.subscriptionSection.activate({ refresh });
  }
}

/**
 * Handle /discovery route
 * Shows discovery page via PageManager and activates DiscoverySection
 * @param {App} app - App instance
 * @param {Object} ctx - page.js context with querystring
 */
async function handleDiscoveryPage(app, ctx) {
  console.log('[Router] Showing discovery page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('discovery');
  }
  if (!activated) return;

  if (app.modules.discoverySection) {
    await app.modules.discoverySection.activate();

    // Always run applyURLParams — empty params is the close signal for the
    // detail panel; tab change pushes also re-fire this handler.
    const urlParams = URLParamsHandler.parse(ctx.querystring, DiscoverySection.urlParamSchema);
    await app.modules.discoverySection.applyURLParams(urlParams.values);
  }
}

/**
 * Handle /partner route
 * Single surface for partner status and management — reachable from the
 * sidebar profile popup's "Partner" / "Link partner" row regardless of link
 * state. The invite CTA when unlinked; when linked, an Overview | Sharing pill
 * pair (`?tab=`) over the identity + master switches and the consent registry.
 * `?share=1` is a one-shot entry intent (the dashboard sharing nudge) that
 * lands on the registry and scrolls it into view.
 * @param {App} app - App instance
 * @param {Object} ctx - page.js context with querystring
 */
async function handlePartnerPage(app, ctx) {
  console.log('[Router] Showing partner page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('partner');
  }
  if (!activated) return;

  if (app.modules.partnerSection) {
    await app.modules.partnerSection.activate();

    const urlParams = URLParamsHandler.parse(ctx.querystring, PartnerSection.urlParamSchema);
    app.modules.partnerSection.applyURLParams(urlParams.values);
  }
}

/**
 * Handle /notes route
 * Shows the couple-gated Notes page and activates PartnerNotesSection.
 * `?list=` selects Inbox/Sent, `?state=` selects Active/Archived, `?note=`
 * opens the detail panel.
 * @param {App} app - App instance
 * @param {Object} ctx - page.js context with querystring
 */
async function handlePartnerNotesPage(app, ctx) {
  console.log('[Router] Showing notes page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('notes');
  }
  if (!activated) return;

  if (app.modules.partnerNotesSection) {
    await app.modules.partnerNotesSection.activate();

    // Always run applyURLParams — empty params is the close signal
    // (browser back from /notes?note=X to /notes).
    const urlParams = URLParamsHandler.parse(ctx.querystring, PartnerNotesSection.urlParamSchema);
    await app.modules.partnerNotesSection.applyURLParams(urlParams.values);
  }
}

/**
 * Handle /bridge route
 * Shows the Bridge placeholder page (static markup, no section).
 * @param {App} app - App instance
 */
async function handleBridgePage(app) {
  console.log('[Router] Showing bridge page');
  app.pendingNavigation = null;

  if (app.modules.pageManager) {
    await app.modules.pageManager.showPage('bridge');
  }
}

/**
 * Handle /account-settings route
 * Placeholder page for profile/account management.
 */
async function handleAccountSettingsPage(app) {
  console.log('[Router] Showing account settings page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('account-settings');
  }
  if (!activated) return;

  if (app.modules.accountSettingsSection) {
    await app.modules.accountSettingsSection.activate();
  }
}

/**
 * Handle /help route
 * Placeholder page for help, support, and legal links.
 */
async function handleHelpPage(app) {
  console.log('[Router] Showing help page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('help');
  }
  if (!activated) return;

  if (app.modules.helpSection) {
    await app.modules.helpSection.activate();
  }
}

/**
 * Handle /memory route
 * Placeholder page for the user-facing view of clinical memory.
 */
async function handleMemoryPage(app) {
  console.log('[Router] Showing memory page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('memory');
  }
  if (!activated) return;

  if (app.modules.memorySection) {
    await app.modules.memorySection.activate();
  }
}

/**
 * Handle /search route
 * Shows search page via PageManager and activates SessionsSection (session notes)
 * @param {App} app - App instance
 * @param {Object} ctx - page.js context with querystring
 */
async function handleSearchPage(app, ctx) {
  console.log('[Router] Showing search page');
  app.pendingNavigation = null;

  let activated = true;
  if (app.modules.pageManager) {
    activated = await app.modules.pageManager.showPage('search');
  }
  if (!activated) return;

  if (app.modules.sessionsSection) {
    await app.modules.sessionsSection.activate();

    // Always run applyURLParams — empty params is the close signal
    // (browser back from /search?session=N to /search).
    const urlParams = URLParamsHandler.parse(ctx.querystring, SessionsSection.urlParamSchema);
    await app.modules.sessionsSection.applyURLParams(urlParams.values);
  }
}

/**
 * Handle /try-free route
 * Dedicated guest entry point for marketing/ad traffic
 * Bypasses login overlay and creates guest session directly
 */
async function handleTryFreeRoute(app) {
  console.log('[Router] /try-free route accessed');

  // If already authenticated (not a guest), redirect to dashboard
  if (app.currentUser && !app.currentUser.isGuest) {
    console.log('[Router] User already authenticated, redirecting to dashboard');
    page.redirect('/');
    return;
  }

  // If already a guest, just redirect to base route
  // This handles the case where checkAuth() already called initializeAsGuest()
  // before the router processed this route
  if (app.currentUser?.isGuest) {
    console.log('[Router] Already a guest, redirecting to /');
    page.redirect('/');
    return;
  }

  // No user at all - initialize as guest and enter app
  try {
    await app.initializeAsGuest();
    page.redirect('/');
  } catch (error) {
    console.error('[Router] Guest initialization failed:', error);
    // Fallback: show login overlay
    app.showLoginOverlay();
  }
}

/**
 * Handle /subscription/success route
 * Post-checkout return from Stripe - polls for subscription activation
 * and navigates to discovery page
 */
async function handleSubscriptionSuccess(app) {
  console.log('[Router] /subscription/success route accessed');

  // Show activating notification
  const notificationId = window.notificationManager?.showInfo(
    'Activating your premium subscription...',
    0 // Don't auto-dismiss
  );

  try {
    // Poll for subscription activation with exponential backoff
    const activation = await SubscriptionAPI.waitForSubscriptionActive(6);

    // Force-refresh usage BEFORE routing home so the quota banner clears
    // before the resumed chat renders. Without this, UsageService.initialize()
    // at boot may have raced the Stripe webhook and cached a stale "basic"
    // snapshot, leaving the banner visible until the next message.
    try {
      await app.services?.usage?.initialize?.();
    } catch (usageErr) {
      console.warn('[Router] Usage refresh after activation failed:', usageErr?.message || usageErr);
    }

    // Clear the activating notification
    if (notificationId) {
      window.notificationManager?.dismiss(notificationId);
    }

    // Show success notification
    window.notificationManager?.showSuccess(
      'Premium activated! Your daily message limit is gone.',
      6000
    );

    // Emit analytics event, enriched from the polled activation status:
    // TRIALING → a trial started (no charge yet), and the partner-rate flag
    // marks a couples-discounted subscription. AnalyticsService forwards
    // both as GA4 params on `subscription_activated`.
    const activatedStatus = activation?.data;
    eventBus.emit('subscription:activated', {
      trial: activatedStatus?.status === 'TRIALING',
      partner_rate: Boolean(activatedStatus?.partnerRate?.applied)
    });

  } catch (error) {
    console.error('[Router] Subscription activation polling failed:', error);

    // Clear the activating notification
    if (notificationId) {
      window.notificationManager?.dismiss(notificationId);
    }

    // Show helpful message - webhook may just be delayed
    window.notificationManager?.showInfo(
      'Your subscription is processing — it may take a moment to activate. Refresh to check.',
      8000
    );
  }

  // Navigate to base route (shows chat if active session, otherwise dashboard)
  page('/');
}
